import React, { useState } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes, FaTh } from "react-icons/fa";

const ModernPhotoGallery = ({ images, title = "Property Photos" }) => {
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Robust fallback logic
  const fallbackImg = "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=1200";

  if (!images || images.length === 0) return null;

  // Use the first 5 images for the grid
  const displayImages = images.slice(0, 5);
  const remainingCount = images.length > 5 ? images.length - 5 : 0;

  const openModal = (index) => {
    setCurrentIndex(index);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="modern-gallery-container" style={{ width: "100%", margin: "0 auto" }}>
      {title && (
        <h2 style={{
          fontSize: "20px",
          fontWeight: "800",
          marginBottom: "15px",
          color: "#1a1a1a",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "0 5px"
        }}>
          <FaTh style={{ color: "#EE2E24", fontSize: "18px" }} /> {title}
        </h2>
      )}

      {/* Grid Layout - Compacted */}
      <div
        className="modern-gallery-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gridTemplateRows: "repeat(2, 140px)",
          gap: "8px",
          borderRadius: "12px",
          overflow: "hidden",
          position: "relative",
          cursor: "pointer",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
        }}
      >
        {/* Main large image */}
        <div
          onClick={() => openModal(0)}
          className="gallery-main-item"
          style={{
            gridColumn: "span 2",
            gridRow: "span 2",
            position: "relative",
            overflow: "hidden"
          }}
        >
          <img
            src={images[0]?.photoUrl || images[0] || fallbackImg}
            alt="Main room"
            onError={(e) => {
              if (e.target.src !== fallbackImg) e.target.src = fallbackImg;
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)"
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.05)"}
            onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
          />
        </div>

        {/* Smaller images */}
        {displayImages.slice(1).map((img, idx) => (
          <div
            key={idx}
            className={`gallery-sub-item item-${idx + 1}`}
            onClick={() => openModal(idx + 1)}
            style={{
              position: "relative",
              overflow: "hidden"
            }}
          >
            <img
              src={img?.photoUrl || img || fallbackImg}
              alt={`Room view ${idx + 2}`}
              onError={(e) => {
                if (e.target.src !== fallbackImg) e.target.src = fallbackImg;
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)"
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = "scale(1.08)"}
              onMouseOut={(e) => e.currentTarget.style.transform = "scale(1)"}
            />
            {idx === 3 && remainingCount > 0 && (
              <div style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.45)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                backdropFilter: "blur(3px)",
                transition: "all 0.3s ease"
              }}
                onMouseOver={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.55)"}
                onMouseOut={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.45)"}
              >
                <span style={{ fontSize: "24px", fontWeight: "900" }}>+{remainingCount}</span>
                <span style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px" }}>Photos</span>
              </div>
            )}
          </div>
        ))}

        {/* Improved Show All button */}
        <button
          onClick={() => openModal(0)}
          style={{
            position: "absolute",
            bottom: "12px",
            right: "12px",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(5px)",
            color: "#EE2E24",
            border: "none",
            padding: "8px 15px",
            borderRadius: "8px",
            fontWeight: "800",
            fontSize: "0.75rem",
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s"
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          <FaTh /> VIEW ALL
        </button>
      </div>

      {/* Full-Screen Modal Viewer */}
      {
        showModal && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.98)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            backdropFilter: "blur(10px)"
          }}>
            {/* Header */}
            <div style={{
              padding: "20px 30px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "#fff",
              zIndex: 10
            }}>
              <span style={{
                fontSize: "18px",
                fontWeight: "600",
                fontFamily: "'Inter', sans-serif",
                letterSpacing: "1px"
              }}>
                {currentIndex + 1} / {images.length}
              </span>
              <button
                onClick={closeModal}
                style={{
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "#EE2E24";
                  e.currentTarget.style.transform = "rotate(90deg)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.15)";
                  e.currentTarget.style.transform = "rotate(0deg)";
                }}
              >
                <FaTimes size={22} />
              </button>
            </div>

            {/* Main Content */}
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: "20px"
            }}>
              <button
                onClick={prevImage}
                style={{
                  position: "absolute",
                  left: "30px",
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(5px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 10,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
              >
                <FaChevronLeft size={24} />
              </button>

              <img
                src={images[currentIndex]?.photoUrl || images[currentIndex]}
                alt="Full view"
                style={{
                  maxWidth: "90%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.8)"
                }}
              />

              <button
                onClick={nextImage}
                style={{
                  position: "absolute",
                  right: "30px",
                  background: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(5px)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  zIndex: 10,
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.1)"}
              >
                <FaChevronRight size={24} />
              </button>
            </div>

            {/* Thumbnail Strip */}
            <div style={{
              padding: "30px",
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              overflowX: "auto",
              scrollbarWidth: "none",
              background: "rgba(0,0,0,0.5)"
            }}>
              {images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: "70px",
                    height: "46px",
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => setCurrentIndex(idx)}
                >
                  <img
                    src={img?.photoUrl || img}
                    alt={`Thumbnail ${idx}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "6px",
                      opacity: currentIndex === idx ? 1 : 0.4,
                      border: currentIndex === idx ? "2px solid #EE2E24" : "1px solid transparent",
                      transition: "all 0.3s ease",
                      transform: currentIndex === idx ? "translateY(-5px)" : "none"
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )
      }

      {/* Mobile view responsive - refined strategy */}
      <style>{`
        @media (max-width: 768px) {
          .modern-gallery-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            grid-template-rows: 240px 120px !important;
            border-radius: 12px !important;
            gap: 6px !important;
          }
          .gallery-main-item {
            grid-column: span 2 !important;
            grid-row: span 1 !important;
          }
          .gallery-sub-item {
            display: block !important;
            grid-column: span 1 !important;
            grid-row: span 1 !important;
          }
          .gallery-sub-item.item-3, 
          .gallery-sub-item.item-4 {
            display: none !important;
          }
          .modern-gallery-container h2 {
            font-size: 20px !important;
            margin-bottom: 15px !important;
          }
        }
      `}</style>
    </div >
  );
};

export default ModernPhotoGallery;
